import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { RootState, Item } from "../../redux/type";
import {
  createItem,
  getAdminItems,
  updateItem,
  deleteItem,
} from "../../api/admin";
import toast from "react-hot-toast";
import AdminNav from "../../components/Admin/AdminNav";
import { MarginBottom } from "../../components/MarginBottom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Check, Edit, Trash2 } from "lucide-react";

// --- Reusable Styled Components for Tabs & Modals ---
const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 2rem;
`;
const TabButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  color: ${(props) => (props.active ? "#fff" : "rgba(255, 255, 255, 0.5)")};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
  &:after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: #ccff00;
    transform: ${(props) => (props.active ? "scaleX(1)" : "scaleX(0)")};
    transition: transform 0.3s ease-out;
  }
`;
const ModalBackdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;
const ModalContent = styled(motion.div)`
  background: #1e1e1e;
  padding: 2rem;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;
const ModalTitle = styled.h3`
  color: #ccff00;
  margin: 0;
  font-size: 1.3rem;
`;
const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`;
const ModalText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;
const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;
const ActionButton = styled.button<{
  $variant?: "default" | "danger" | "success";
}>`
  padding: 0.5rem 0.9rem;
  border-radius: 6px;
  border: none;
  background: ${(props) => {
    switch (props.$variant) {
      case "danger":
        return "linear-gradient(135deg, #ff4d4d, #cc0000)";
      case "success":
        return "linear-gradient(135deg, #00cc66, #00994d)";
      default:
        return "rgba(255, 255, 255, 0.15)";
    }
  }};
  color: #fff;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.25s ease-in-out;
  font-weight: 500;

  /* Flexbox for proper icon + text alignment */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 2rem;
  line-height: 1;

  /* Icon styling */
  svg {
    width: 1em;
    height: 1em;
    flex-shrink: 0;
  }

  /* Adjust padding when icon is present */
  &:has(svg:first-child:not(:only-child)) {
    padding-left: 0.7rem;
  }

  &:has(svg:last-child:not(:only-child)) {
    padding-right: 0.7rem;
  }

  &:hover:not(:disabled) {
    background: ${(props) => {
      switch (props.$variant) {
        case "danger":
          return "linear-gradient(135deg, #ff2d2d, #990000)";
        case "success":
          return "linear-gradient(135deg, #00b359, #008040)";
        default:
          return "rgba(255, 255, 255, 0.25)";
      }
    }};
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.6rem 0.7rem;
    gap: 0.3rem;
    min-height: 1.875rem;

    &:has(svg:first-child:not(:only-child)) {
      padding-left: 0.6rem;
    }

    &:has(svg:last-child:not(:only-child)) {
      padding-right: 0.6rem;
    }
  }
`;
// --- Page Specific Styled Components ---
const Container = styled.div`
  padding: 1.5rem;
  max-width: 900px;
  margin: 0 auto;
`;
const Title = styled.h2`
  color: #ccff00;
  text-align: center;
  font-size: 1.8rem;
  margin-bottom: 2rem;
`;
const FormContainer = styled.div`
  background: #1a1a1a;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;
const FormGroup = styled.div`
  margin-bottom: 1.2rem;
`;
const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #ccff00;
  }
`;
const Button = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #ccff00, #3b82f6);
  color: #111;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const ItemsTableContainer = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: #fff;
`;
const Th = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #ccff00;
  background: rgba(255, 255, 255, 0.05);
`;
const Td = styled.td`
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;
const MobileActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const AdminItems = () => {
  const user = useSelector((state: RootState) => state.userData);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("add");
  const [editModalItem, setEditModalItem] = useState<Item | null>(null);
  const [deleteModalItem, setDeleteModalItem] = useState<Item | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: 0,
    image: "",
    stock: "",
    stickerPackUrl: "",
  });

  const fetchItems = async () => {
    setLoading(true);
    try {
      if (user?.uid) {
        const adminItems = await getAdminItems(user.uid);
        setItems(adminItems);
      }
    } catch (error) {
      toast.error("Could not fetch items.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "view") {
      fetchItems();
    }
  }, [user, activeTab]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (editModalItem) {
      setEditModalItem({ ...editModalItem, [name]: value });
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const toastId = toast.loading("Adding new item...");
    try {
      const newItemData = {
        name: formData.name,
        description: formData.description,
        price: Number(formData.price),
        image: formData.image,
        stock: formData.stock ? Number(formData.stock) : undefined,
        stickerPackUrl: formData.stickerPackUrl || undefined,
      };
      // @ts-ignore
      await createItem(user.uid, newItemData);
      toast.success("Item added successfully!", { id: toastId });
      setFormData({
        name: "",
        description: "",
        price: 0,
        image: "",
        stock: "",
        stickerPackUrl: "",
      });
      setActiveTab("view");
    } catch (error: any) {
      toast.error(error.message, { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubmit = async () => {
    if (!editModalItem) return;
    setLoading(true);
    const toastId = toast.loading("Updating item...");
    try {
      await updateItem(user.uid, editModalItem._id, editModalItem);
      toast.success("Item updated!", { id: toastId });
      setEditModalItem(null);
      fetchItems();
    } catch (error: any) {
      toast.error(error.message, { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModalItem) return;
    setLoading(true);
    const toastId = toast.loading("Deactivating item...");
    try {
      await deleteItem(user.uid, deleteModalItem._id);
      toast.success("Item deactivated!", { id: toastId });
      setDeleteModalItem(null);
      fetchItems();
    } catch (error: any) {
      toast.error(error.message, { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AdminNav />
      <Container>
        <Title>Manage Store Items</Title>

        <TabsContainer>
          <TabButton
            active={activeTab === "add"}
            onClick={() => setActiveTab("add")}
          >
            Add New Item
          </TabButton>
          <TabButton
            active={activeTab === "view"}
            onClick={() => setActiveTab("view")}
          >
            View All Items
          </TabButton>
        </TabsContainer>

        {activeTab === "add" && (
          <FormContainer>
            <form onSubmit={handleCreateSubmit}>
              <FormGroup>
                <Label>Item Name</Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Description</Label>
                <Input
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Price (in XP)</Label>
                <Input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  min="0"
                />
              </FormGroup>
              <FormGroup>
                <Label>Image URL</Label>
                <Input
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              {/* <FormGroup>
                <Label>Stock (optional)</Label>
                <Input
                  type="number"
                  name="stock"
                  value={formData.stock}
                  onChange={handleInputChange}
                  min="0"
                />
              </FormGroup> */}
              <FormGroup>
                <Label>Telegram Sticker Pack URL (optional)</Label>
                <Input
                  name="stickerPackUrl"
                  value={formData.stickerPackUrl}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <Button type="submit" disabled={loading}>
                {loading ? "Adding..." : "Add Item"}
              </Button>
            </form>
          </FormContainer>
        )}

        {activeTab === "view" && (
          <ItemsTableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Price (XP)</Th>
                  {/* <Th>Stock</Th> */}
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <Td colSpan={4} style={{ textAlign: "center" }}>
                      <Loader2 className="animate-spin" />
                    </Td>
                  </tr>
                ) : items.length > 0 ? (
                  items.map((item) => (
                    <tr key={item._id}>
                      <Td>{item.name}</Td>
                      <Td>{item.price.toLocaleString()}</Td>
                      {/* <Td>{item.stock ?? "Unlimited"}</Td> */}
                      <Td>
                        <MobileActions>
                          <ActionButton onClick={() => setEditModalItem(item)}>
                            Details
                          </ActionButton>
                        </MobileActions>
                      </Td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <Td colSpan={4} style={{ textAlign: "center" }}>
                      No items found.
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ItemsTableContainer>
        )}
      </Container>
      <MarginBottom />

      {/* --- Edit Modal --- */}
      <AnimatePresence>
        {editModalItem && (
          <ModalBackdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setEditModalItem(null)}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>Edit {editModalItem.name}</ModalTitle>
                <CloseButton onClick={() => setEditModalItem(null)}>
                  <X size={20} />
                </CloseButton>
              </ModalHeader>

              <EditForm>
                <FormGroup>
                  <Label>Name</Label>
                  <Input
                    name="name"
                    value={editModalItem.name}
                    onChange={handleEditInputChange}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    name="description"
                    value={editModalItem.description}
                    onChange={handleEditInputChange}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Price (XP)</Label>
                  <Input
                    type="number"
                    name="price"
                    value={editModalItem.price}
                    onChange={handleEditInputChange}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Image URL</Label>
                  <Input
                    name="image"
                    value={editModalItem.image}
                    onChange={handleEditInputChange}
                  />
                </FormGroup>
                {/* <FormGroup>
                  <Label>Stock</Label>
                  <Input
                    type="number"
                    name="stock"
                    value={editModalItem.stock || ""}
                    onChange={handleEditInputChange}
                    placeholder="Unlimited"
                  />
                </FormGroup> */}
                <FormGroup>
                  <Label>Sticker Pack URL</Label>
                  <Input
                    name="stickerPackUrl"
                    value={editModalItem.stickerPackUrl || ""}
                    onChange={handleEditInputChange}
                  />
                </FormGroup>
              </EditForm>

              <ModalButtons>
                <ActionButton
                  onClick={() => {
                    setDeleteModalItem(editModalItem);
                    setEditModalItem(null);
                  }}
                  $variant="danger"
                >
                  <Trash2 size={16} />
                  Delete
                </ActionButton>
                <div style={{ display: "flex", gap: "0.75rem" }}>
                  <ActionButton
                    onClick={handleUpdateSubmit}
                    disabled={loading}
                    $variant="success"
                  >
                    {loading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Check size={16} />
                    )}
                    {loading ? "Saving..." : "Save Changes"}
                  </ActionButton>
                </div>
              </ModalButtons>
            </ModalContent>
          </ModalBackdrop>
        )}

        {/* --- Delete Confirmation Modal --- */}
        {deleteModalItem && (
          <ModalBackdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDeleteModalItem(null)}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>Confirm Deactivation</ModalTitle>
                <CloseButton onClick={() => setDeleteModalItem(null)}>
                  <X size={20} />
                </CloseButton>
              </ModalHeader>

              <ModalText>
                Are you sure you want to deactivate{" "}
                <strong>"{deleteModalItem.name}"</strong>? Users will no longer
                see it in the store.
              </ModalText>

              <ModalButtons>
                <ActionButton
                  onClick={handleDelete}
                  disabled={loading}
                  $variant="danger"
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                  {loading ? "Deactivating..." : "Deactivate"}
                </ActionButton>
              </ModalButtons>
            </ModalContent>
          </ModalBackdrop>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminItems;
